/**
 * Eligibility checks for the SSO → Provider Portal Create-Auth pre-flight
 * (ACT-1247 SSO-PP auth-linking Phase 1, design doc §5a).
 *
 * An SSO/Pathways sign-in does not imply Provider Portal capability. Two
 * independent things can be false even once a persisted PP link exists
 * (see auth/ppToken.ts's `hasPersistedPpLink` / `ensurePpAccessToken`):
 *
 *   1. USER eligibility — the linked PP account may not carry the
 *      `Authorization` permission or an IPA-group membership, which is what
 *      actually clears APIM's `QA.ProviderPortal.<IPA>` named-group claim
 *      check. `claimsIndicateUserEligibility` is a best-effort, claims-only
 *      guess (see its doc comment for why it's a guess, not ground truth).
 *   2. PATIENT eligibility — the current patient may have no EZCAP member
 *      record in the user's IPA/company. `checkPatientEligibility` confirms
 *      this via the SAME `AuthMember/GetAuthMember` endpoint the native
 *      Create-Auth module's own hydration query already uses.
 *
 * `checkPatientEligibility`'s HTTP response is the REAL ground truth for
 * both signals (a 401/403 means APIM rejected the token before EZCAP was
 * ever reached — i.e. a user-eligibility failure, not just a patient one),
 * and its result should override a claims-only guess when they disagree —
 * in BOTH directions: a 401/403 downgrades a claims-only "eligible" guess to
 * ineligible, and conversely a 200 response (with or without rows — a 200
 * proves the token cleared APIM regardless of row count) upgrades a
 * claims-only "ineligible" guess to eligible. `claimsIndicateUserEligibility`
 * is a fast HINT only and must never be the sole/final gate — see
 * members/usePpCreateAuthPreflight.ts for how the two are combined.
 */

export type PpEligibilityReason =
  | "unlinked"
  | "no-pp-permission"
  | "member-not-found"
  | "not-pp-eligible"
  | "preflight-error";

export const PP_ELIGIBILITY_REASON_TEXT: Record<PpEligibilityReason, string> = {
  unlinked: "Sign in to Provider Portal to continue.",
  "no-pp-permission":
    "This account isn't set up for Provider Portal authorizations (no Authorization permission / IPA group).",
  "member-not-found": "This member could not be found in EZCAP for your Provider Portal IPA.",
  "not-pp-eligible":
    "This member doesn't have a Provider Portal / EZCAP company context to verify Create Auth access.",
  "preflight-error": "Couldn't verify Provider Portal access right now. Try signing in again.",
};

interface PpJwtEligibilityClaims {
  groups?: string[];
  extension_Groups?: string[] | string;
  roles?: string[] | string;
  role?: string;
  extension_Role?: string;
  permissions?: string[] | string;
}

// The design doc's own shape: "QA.ProviderPortal.<IPA>". Matched
// case-insensitively against whatever group-ish claim the token carries.
const IPA_GROUP_PATTERN = /providerportal/i;
const AUTHORIZATION_PERMISSION_PATTERN = /authorization/i;

function asArray(value: string[] | string | undefined): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

/**
 * Best-effort, claims-only signal for whether a minted PP access token
 * carries the `Authorization` permission and >=1 IPA-group membership.
 *
 * GAP (flagged deliberately, same convention as members/toAuthMember.ts):
 * the real claim name(s) PP's B2C policy emits for "IPA group" / permission
 * have never been confirmed against a live token in this repo — the only PP
 * claims verified so far are `extension_Role` / generic `roles` (see
 * auth/jwt.ts). This checks the most likely candidate claim names
 * (`groups`/`extension_Groups` for IPA groups, `roles`/`permissions`/
 * `role`/`extension_Role` for the Authorization permission) as a fast,
 * cacheable pre-check — it is NEVER the sole gate: a false positive here is
 * caught by `checkPatientEligibility`'s real APIM round trip, whose
 * 401/403 is ground truth and overrides this guess (see
 * members/usePpCreateAuthPreflight.ts). If a future engineer confirms the
 * real claim shape, tighten this pattern match accordingly.
 */
export function claimsIndicateUserEligibility(
  claims: Record<string, unknown> | null,
): { eligible: boolean; ipaGroups: string[] } {
  if (!claims) return { eligible: false, ipaGroups: [] };
  const c = claims as PpJwtEligibilityClaims;
  const groupCandidates = [...asArray(c.groups), ...asArray(c.extension_Groups)];
  const ipaGroups = groupCandidates.filter((g) => IPA_GROUP_PATTERN.test(g));
  const permissionCandidates = [
    ...asArray(c.roles),
    ...asArray(c.permissions),
    ...(c.role ? [c.role] : []),
    ...(c.extension_Role ? [c.extension_Role] : []),
  ];
  const hasAuthorizationPermission = permissionCandidates.some((p) =>
    AUTHORIZATION_PERMISSION_PATTERN.test(p),
  );
  return { eligible: ipaGroups.length > 0 && hasAuthorizationPermission, ipaGroups };
}

export interface PatientEligibilityArgs {
  /** Provider Portal API base URL WITHOUT a trailing `/api` — this function
   *  appends `/api/AuthMember/GetAuthMember` itself (mirrors
   *  @astrana/ui's `mintHandoffToken` URL convention, not the `/api`-suffixed
   *  `ppApiBaseUrl` prop MemberProfilePage feeds the Authorizations module). */
  ppApiBaseUrl: string;
  accessToken: string;
  companyId: string;
  memberId: string;
  userId?: string;
  userRole?: string;
}

export type PatientEligibilityFailureReason = Extract<
  PpEligibilityReason,
  "no-pp-permission" | "member-not-found" | "preflight-error"
>;

export interface PatientEligibilityResult {
  eligible: boolean;
  reason?: PatientEligibilityFailureReason;
}

/**
 * AM-5713 follow-up (2026-07-21): a prod wire capture proved the assumption
 * behind the old `EMPTY_GUID` constant here was backwards. The BE
 * `AuthMemberRequestModel.ProvKeyId` is non-nullable, so a literal JSON
 * `null` does fail model-binding with an EZCap-shaped HTTP 400 — but
 * Provider Portal's own working Create-Auth flow does NOT send the all-zero
 * GUID either; it sends the empty STRING `""`, which the BE accepts (200).
 * Sending the all-zero GUID instead causes the BE to hang/time out (504) —
 * likely because a syntactically-valid-but-nonexistent Guid drives a slower
 * provider-key lookup/join that a genuinely-blank filter skips entirely.
 * `""` is the value proven safe by the working Provider-Portal login; use it
 * here too instead of re-introducing the zero-GUID this preflight call
 * would otherwise send on every eligible SSO/Pathways patient check.
 */
const NO_PROVIDER_FILTER = "";

/**
 * In-flight dedup for `checkPatientEligibility` (2026-07-24, duplicate-
 * PP-API-calls investigation) — mirrors `b2cClient.ts`'s `ropcRefresh`
 * in-flight-promise-Map pattern verbatim (same shape: key → in-progress
 * promise, delete-if-still-current in `finally`).
 *
 * DIAGNOSIS: a user-visible "duplicate GetAuthMember" call is real, but its
 * cause is NOT member-id casing — this repo has no `.toUpperCase()`/
 * `.toLowerCase()` transform anywhere on companyId/memberId, and
 * MemberProfilePage.tsx feeds the IDENTICAL `companyId`/`handoffMemberIdHint`
 * values to both this preflight call and the Authorizations module's
 * `companyId`/`memberIdHint` props (verified by reading the call sites side
 * by side) — no casing/value divergence exists at the host boundary. The
 * real cause is TWO DISTINCT CALLERS hitting the identical
 * `AuthMember/GetAuthMember` endpoint BY DESIGN: this function (a hand-rolled
 * `fetch`, deliberately NOT RTK Query, since it must run before the module
 * ever mounts) and the module's OWN internal hydration query
 * (astrana-ui-modules' `createAuthApi.searchAuthMember` — confirmed in the
 * shipped bundle: `build.query({ query: () => ({ url:
 * "AuthMember/GetAuthMember", method: "POST" ... }) })`, generated hook
 * `useSearchAuthMemberQuery`). That fork is the documented, intentional
 * "fail up-front before the create surface even renders" vs. "the module's
 * own list hydration once mounted" split (see this file's own header
 * comment + design doc §5a) — fully unifying it needs an UPSTREAM
 * (astrana-ui-modules) change so the module can accept/reuse a pre-fetched
 * result, which is out of scope for a host-only fix and is NOT attempted
 * here (forcing it would mean either delaying the Auths tab's render on an
 * extra round trip, or an upstream API this module doesn't expose today).
 *
 * What IS safely fixable from here: THIS function's own call has ZERO
 * dedup — unlike the module's RTK-Query-backed calls (which already dedupe
 * identical concurrent subscriptions for free, including across React 18
 * StrictMode's dev-only double-effect-invoke), a bare hand-rolled `fetch()`
 * has no such protection, so StrictMode alone (confirmed active in
 * `main.tsx`) — or any other rapid remount/re-render churn with identical
 * args — was firing THIS call twice on its own, on top of the module's
 * separate call. The Map below collapses any concurrent call with the exact
 * same (ppApiBaseUrl, companyId, memberId, accessToken, userId, userRole)
 * into a single real network request, cutting the AVOIDABLE half of the
 * duplication without touching the eligibility gate's semantics (identical
 * inputs still produce identical outputs) or the module's own separate call.
 */
const inFlightEligibilityChecks = new Map<string, Promise<PatientEligibilityResult>>();

function eligibilityCacheKey(args: PatientEligibilityArgs): string {
  return [
    args.ppApiBaseUrl,
    args.companyId,
    args.memberId,
    args.accessToken,
    args.userId ?? "",
    args.userRole ?? "",
  ].join("|");
}

/**
 * Confirms an EZCAP member exists for the current patient by calling the
 * SAME `AuthMember/GetAuthMember` endpoint the native Create-Auth module's
 * own hydration query uses (astrana-ui-modules `useCreateAuthInit`'s
 * `searchAuthMember`/`useSearchAuthMemberQuery`) — see design doc §5a. See
 * `inFlightEligibilityChecks`'s doc comment above for the full duplicate-
 * calls diagnosis and why THIS caller (but not the module's own, separate
 * call) can be safely deduped from the host side.
 *
 *   - HTTP 401/403 → APIM rejected the token before EZCAP was ever reached:
 *     real ground truth for user-eligibility (stronger than
 *     `claimsIndicateUserEligibility`'s guess).
 *   - HTTP 200 with zero rows → the token cleared APIM but no EZCAP member
 *     exists for this patient in the user's IPA/company.
 *   - HTTP 200 with >=1 row → both pass.
 *   - Anything else (network error, non-JSON body, missing args) → fails
 *     CLOSED with `"preflight-error"` per the design doc's non-negotiable.
 */
export async function checkPatientEligibility(
  args: PatientEligibilityArgs,
): Promise<PatientEligibilityResult> {
  if (!args.ppApiBaseUrl || !args.companyId || !args.memberId || !args.accessToken) {
    return { eligible: false, reason: "preflight-error" };
  }

  const key = eligibilityCacheKey(args);
  const existing = inFlightEligibilityChecks.get(key);
  if (existing) return existing;

  const promise = runPatientEligibilityCheck(args);
  inFlightEligibilityChecks.set(key, promise);
  try {
    return await promise;
  } finally {
    // Only clear if we're still the in-flight entry — a caller that raced in
    // after this one settled but before cleanup ran could otherwise clobber
    // a newer in-flight promise's map entry (same guard as ropcRefresh's).
    if (inFlightEligibilityChecks.get(key) === promise) {
      inFlightEligibilityChecks.delete(key);
    }
  }
}

async function runPatientEligibilityCheck(
  args: PatientEligibilityArgs,
): Promise<PatientEligibilityResult> {
  try {
    const res = await fetch(`${args.ppApiBaseUrl}/api/AuthMember/GetAuthMember`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // BARE token, no "Bearer " scheme prefix — this endpoint reads the
        // Authorization header verbatim and feeds it to
        // JwtSecurityTokenHandler.ReadJwtToken, which rejects a "Bearer "
        // prefix ("JWT is not well formed", HTTP 500). Matches
        // astrana-ui-modules' own baseQuery.ts (store/baseQuery.ts
        // prepareHeaders), the ONLY other caller of this exact endpoint —
        // see that file's identical comment. Other PP endpoints don't parse
        // the token, so both forms worked there; only GetAuthMember exposed
        // the mismatch, which 500'd this preflight for every linked-SSO user
        // and hard-blocked Create Auth before the module's own (correctly
        // bare) hydration call ever ran.
        Authorization: args.accessToken,
        "X-Backend-Id": args.companyId,
      },
      body: JSON.stringify({
        AuthMemberSearchModel: {
          CompanyId: args.companyId,
          MemberId: args.memberId,
          ProvKeyId: NO_PROVIDER_FILTER,
          DateOfBirth: null,
          FirstName: null,
          LastName: null,
          RetroDOS: null,
          PageIndex: 0,
          PageSize: 1,
        },
        UserIdModel: {
          UserId: args.userId ?? null,
          UserRole: args.userRole ?? null,
        },
      }),
    });

    if (res.status === 401 || res.status === 403) {
      return { eligible: false, reason: "no-pp-permission" };
    }
    if (!res.ok) return { eligible: false, reason: "preflight-error" };

    const body: unknown = await res.json().catch(() => null);
    const rows = Array.isArray(body)
      ? body
      : Array.isArray((body as { value?: unknown[] } | null)?.value)
        ? (body as { value: unknown[] }).value
        : null;
    if (rows === null) return { eligible: false, reason: "preflight-error" };
    return rows.length > 0 ? { eligible: true } : { eligible: false, reason: "member-not-found" };
  } catch {
    return { eligible: false, reason: "preflight-error" };
  }
}
